import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Copy, RotateCw, ShieldAlert, TriangleAlert, WifiOff, SearchX } from 'lucide-react'
import { cn } from '@/lib/cn'
import { describeError, type ErrorInfo } from '@/lib/apiError'
import { Button } from './Button'

export interface ErrorStateProps {
  /** The thrown error (an ApiError, AxiosError or Error). */
  error: unknown
  /** Refetch only what failed. Omit when retrying cannot help (403). */
  onRetry?: () => void
  retrying?: boolean
  /** Secondary escape, e.g. back to the parent list. */
  back?: { to: string; label: string }
  /** `panel` fills a page/card; `inline` sits inside a table body or a small card. */
  variant?: 'panel' | 'inline'
  /** Override the heading, e.g. "Couldn't load leave requests". */
  title?: string
  /** Override the sentence, e.g. for a render crash whose raw message is not for users. */
  message?: string
  className?: string
}

function iconFor(info: ErrorInfo) {
  if (info.kind === 'network') return WifiOff
  if (info.kind === 'forbidden') return ShieldAlert
  if (info.kind === 'not_found') return SearchX
  return TriangleAlert
}

/** Reference id for support, with a copy button (spec 21 §3.9.3). */
export function TraceId({ id, className }: { id: string; className?: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard blocked: the id is still selectable */ }
  }
  return (
    <div className={cn('inline-flex max-w-full items-center gap-1.5 text-xs text-muted-foreground', className)}>
      <span className="shrink-0">Reference</span>
      <code className="min-w-0 select-all truncate rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-content-secondary" title={id}>
        {id}
      </code>
      <button
        type="button"
        onClick={copy}
        className="grid size-6 shrink-0 place-items-center rounded text-muted-foreground transition-colors duration-fast hover:bg-surface-hover hover:text-foreground"
        aria-label={copied ? 'Reference copied' : 'Copy reference'}
        title={copied ? 'Copied' : 'Copy reference'}
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </button>
    </div>
  )
}

/**
 * The error state — deliberately different from the empty state: danger-toned icon, a
 * plain-language cause, Retry, and the API trace id. A failed request must never look like
 * "no records" (spec 21 §3.9.3, G-16).
 */
export function ErrorState({ error, onRetry, retrying, back, variant = 'panel', title, message, className }: ErrorStateProps) {
  const info = describeError(error)
  const Icon = iconFor(info)
  const canRetry = onRetry && info.kind !== 'forbidden' && info.kind !== 'not_found'
  const inline = variant === 'inline'
  return (
    <div
      role="alert"
      data-state="error"
      className={cn(
        'flex flex-col items-center justify-center text-center',
        inline ? 'px-4 py-8' : 'rounded-lg border border-border bg-card px-6 py-12 shadow-e1',
        className,
      )}
    >
      <div className="mb-3 grid size-11 place-items-center rounded-xl bg-danger-subtle text-danger-content">
        <Icon className="size-5" />
      </div>
      <h3 className="text-sm font-semibold text-foreground">{title ?? info.title}</h3>
      <p className="mt-1 max-w-md text-[13px] leading-5 text-muted-foreground">{message ?? info.message}</p>
      {(canRetry || back) && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {canRetry && (
            <Button size="sm" variant="outline" icon={<RotateCw />} loading={retrying} onClick={onRetry}>
              Try again
            </Button>
          )}
          {back && (
            <Link to={back.to} className="inline-flex h-8 items-center rounded-md px-3 text-[13px] font-medium text-link hover:underline">
              {back.label}
            </Link>
          )}
        </div>
      )}
      {info.traceId && <TraceId id={info.traceId} className="mt-4" />}
    </div>
  )
}
